import { create } from 'zustand';
import { supabase } from '@/lib/supabase';
import type { Property, PropertyStatus, PropertyType, InvestmentType, PropertyFormData, PropertyImage } from '@/types';
import { eurosToCents } from '@/lib/utils';
import { db } from '@/lib/offline-db';

interface PropertyFilters {
  status?: PropertyStatus | 'all';
  property_type?: PropertyType | 'all';
  investment_type?: InvestmentType | 'all';
  search?: string;
}

interface PropertyState {
  properties: Property[];
  currentProperty: Property | null;
  filters: PropertyFilters;
  isLoading: boolean;
  error: string | null;
  subscription: ReturnType<typeof supabase.channel> | null;

  fetchProperties: () => Promise<void>;
  fetchProperty: (id: string) => Promise<void>;
  createProperty: (data: PropertyFormData) => Promise<Property>;
  updateProperty: (id: string, data: Partial<PropertyFormData>) => Promise<void>;
  deleteProperty: (id: string) => Promise<void>;
  setFilters: (filters: PropertyFilters) => void;
  clearError: () => void;
  subscribeToChanges: () => void;
  unsubscribe: () => void;
}

export const usePropertyStore = create<PropertyState>((set, get) => ({
  properties: [],
  currentProperty: null,
  filters: { status: 'all' },
  isLoading: false,
  error: null,
  subscription: null,

  fetchProperties: async () => {
    set({ isLoading: true, error: null });
    try {
      let query = supabase
        .from('properties')
        .select(`
          *,
          financings(*),
          property_images(*)
        `)
        .order('created_at', { ascending: false });

      const { filters } = get();
      if (filters.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }
      if (filters.property_type && filters.property_type !== 'all') {
        query = query.eq('property_type', filters.property_type);
      }
      if (filters.investment_type && filters.investment_type !== 'all') {
        query = query.eq('investment_type', filters.investment_type);
      }
      if (filters.search) {
        query = query.ilike('name', `%${filters.search}%`);
      }

      const { data, error } = await query;
      if (error) {
        console.error('fetchProperties query error:', error);
        throw error;
      }

      // Batch generate signed URLs for all images
      if (data && data.length > 0) {
        // Collect all unique storage paths
        const allImages: Array<{ propertyIndex: number; imageIndex: number; storagePath: string }> = [];
        data.forEach((property, pIdx) => {
          if (property.property_images && Array.isArray(property.property_images)) {
            property.property_images.forEach((img: PropertyImage, iIdx: number) => {
              allImages.push({
                propertyIndex: pIdx,
                imageIndex: iIdx,
                storagePath: img.storage_path,
              });
            });
          }
        });

        // Generate signed URLs in parallel
        if (allImages.length > 0) {
          const urlPromises = allImages.map(({ storagePath }) =>
            supabase.storage.from('property-images').createSignedUrl(storagePath, 3600)
          );

          const urlResults = await Promise.all(urlPromises);

          // Map URLs back to images
          allImages.forEach(({ propertyIndex, imageIndex }, idx) => {
            const urlData = urlResults[idx].data;
            if (urlData?.signedUrl && data[propertyIndex].property_images) {
              data[propertyIndex].property_images[imageIndex].url = urlData.signedUrl;
            }
          });
        }

        // Rename property_images to images for consistency
        data.forEach((property) => {
          if (property.property_images) {
            (property as Property).images = property.property_images;
          }
        });
      }

      // Online: Ergebnisse in IndexedDB cachen
      if (data && data.length > 0) {
        await db.properties.bulkPut(
          data.map((p) => ({ id: p.id, owner_id: p.owner_id, data: p as Property, cached_at: Date.now() }))
        );
      }

      set({ properties: data || [], isLoading: false });
    } catch (error) {
      // Offline-Fallback: gecachte Properties aus IndexedDB laden
      if (!navigator.onLine) {
        try {
          const cached = await db.properties.toArray();
          set({ properties: cached.map((c) => c.data), isLoading: false, error: null });
          return;
        } catch {
          // IndexedDB ebenfalls nicht verfügbar
        }
      }
      const message = error instanceof Error ? error.message : 'Fehler beim Laden der Immobilien';
      console.error('fetchProperties error:', error);
      set({ error: message, isLoading: false });
    }
  },

  fetchProperty: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('properties')
        .select(`
          *,
          financings(*),
          shares:property_shares(*, user:profiles!property_shares_user_id_fkey(*)),
          images:property_images(*),
          owner:profiles!properties_owner_id_fkey(*)
        `)
        .eq('id', id)
        .single();

      if (error) throw error;

      if (data?.images) {
        const imagesWithUrls = await Promise.all(
          data.images.map(async (img: PropertyImage) => {
            const { data: urlData } = await supabase.storage
              .from('property-images')
              .createSignedUrl(img.storage_path, 3600);
            return { ...img, url: urlData?.signedUrl };
          })
        );
        data.images = imagesWithUrls;
      }

      // Online: in IndexedDB cachen
      await db.properties.put({ id: data.id, owner_id: data.owner_id, data: data as Property, cached_at: Date.now() });

      set({ currentProperty: data, isLoading: false });
    } catch (error) {
      // Offline-Fallback: gecachte Property aus IndexedDB laden
      if (!navigator.onLine) {
        try {
          const cached = await db.properties.get(id);
          if (cached) {
            set({ currentProperty: cached.data, isLoading: false, error: null });
            return;
          }
        } catch {
          // IndexedDB ebenfalls nicht verfügbar
        }
      }
      const message = error instanceof Error ? error.message : 'Immobilie nicht gefunden';
      console.error('fetchProperty error:', error);
      set({ error: message, isLoading: false });
    }
  },

  createProperty: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('Nicht angemeldet');

      const propertyData = {
        owner_id: userData.user.id,
        name: data.name,
        address_street: data.address_street || null,
        address_zip: data.address_zip || null,
        address_city: data.address_city || null,
        property_type: data.property_type,
        living_area_sqm: data.living_area_sqm || null,
        land_area_sqm: data.land_area_sqm || null,
        year_built: data.year_built || null,
        num_units: data.num_units || 1,
        floor: data.floor || null,
        num_floors: data.num_floors || null,
        status: data.status,
        investment_type: data.investment_type,
        dismissal_reason: data.dismissal_reason || null,
        dismissal_notes: data.dismissal_notes || null,
        deal_source: data.deal_source || null,
        source_contact_name: data.source_contact_name || null,
        source_contact_phone: data.source_contact_phone || null,
        source_contact_email: data.source_contact_email || null,
        purchase_price: eurosToCents(data.purchase_price ?? null),
        additional_costs: eurosToCents(data.additional_costs ?? null),
        current_value: eurosToCents(data.current_value ?? null),
        rent_cold: eurosToCents(data.rent_cold ?? null),
        rent_warm: eurosToCents(data.rent_warm ?? null),
        notes: data.notes || null,
      };

      // Offline: lokal speichern + in Sync-Queue einreihen
      if (!navigator.onLine) {
        const tempId = crypto.randomUUID();
        const tempProperty = {
          ...propertyData,
          id: tempId,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          financings: [],
          images: [],
          shares: [],
        } as unknown as Property;

        await db.properties.put({ id: tempId, owner_id: userData.user.id, data: tempProperty, cached_at: Date.now() });
        await db.sync_queue.add({ action: 'create', record_id: tempId, payload: propertyData as Record<string, unknown>, queued_at: Date.now() });

        set((state) => ({ properties: [tempProperty, ...state.properties], isLoading: false }));
        return tempProperty;
      }

      const { data: newProperty, error } = await supabase
        .from('properties')
        .insert(propertyData)
        .select()
        .single();

      if (error) {
        console.error('Supabase create error:', error);
        throw new Error(error.message || 'Fehler beim Erstellen der Immobilie');
      }

      // Refetch to update the list
      await get().fetchProperties();
      set({ isLoading: false });
      return newProperty;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Erstellen der Immobilie';
      console.error('createProperty error:', error);
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  updateProperty: async (id, data) => {
    // Save current state for rollback
    const previousProperties = get().properties;
    const previousCurrent = get().currentProperty;

    set({ isLoading: true, error: null });
    try {
      const updateData: Record<string, any> = {};
      if (data.name !== undefined) updateData.name = data.name;
      if (data.address_street !== undefined) updateData.address_street = data.address_street || null;
      if (data.address_zip !== undefined) updateData.address_zip = data.address_zip || null;
      if (data.address_city !== undefined) updateData.address_city = data.address_city || null;
      if (data.property_type !== undefined) updateData.property_type = data.property_type;
      if (data.living_area_sqm !== undefined) updateData.living_area_sqm = data.living_area_sqm || null;
      if (data.land_area_sqm !== undefined) updateData.land_area_sqm = data.land_area_sqm || null;
      if (data.year_built !== undefined) updateData.year_built = data.year_built || null;
      if (data.num_units !== undefined) updateData.num_units = data.num_units || 1;
      if (data.floor !== undefined) updateData.floor = data.floor || null;
      if (data.num_floors !== undefined) updateData.num_floors = data.num_floors || null;
      if (data.status !== undefined) updateData.status = data.status;
      if (data.investment_type !== undefined) updateData.investment_type = data.investment_type;
      if (data.dismissal_reason !== undefined) updateData.dismissal_reason = data.dismissal_reason || null;
      if (data.dismissal_notes !== undefined) updateData.dismissal_notes = data.dismissal_notes || null;
      if (data.deal_source !== undefined) updateData.deal_source = data.deal_source || null;
      if (data.source_contact_name !== undefined) updateData.source_contact_name = data.source_contact_name || null;
      if (data.source_contact_phone !== undefined) updateData.source_contact_phone = data.source_contact_phone || null;
      if (data.source_contact_email !== undefined) updateData.source_contact_email = data.source_contact_email || null;
      if (data.purchase_price !== undefined) updateData.purchase_price = eurosToCents(data.purchase_price);
      if (data.additional_costs !== undefined) updateData.additional_costs = eurosToCents(data.additional_costs);
      if (data.current_value !== undefined) updateData.current_value = eurosToCents(data.current_value);
      if (data.rent_cold !== undefined) updateData.rent_cold = eurosToCents(data.rent_cold);
      if (data.rent_warm !== undefined) updateData.rent_warm = eurosToCents(data.rent_warm);
      if (data.notes !== undefined) updateData.notes = data.notes || null;

      // Optimistic update - update UI immediately
      set((state) => ({
        properties: state.properties.map((p) =>
          p.id === id ? { ...p, ...updateData } : p
        ),
        currentProperty:
          state.currentProperty?.id === id
            ? { ...state.currentProperty, ...updateData }
            : state.currentProperty,
      }));

      // Offline: in IndexedDB aktualisieren + in Sync-Queue einreihen
      if (!navigator.onLine) {
        const cached = await db.properties.get(id);
        if (cached) {
          const updatedData = { ...cached.data, ...updateData, updated_at: new Date().toISOString() };
          await db.properties.put({ ...cached, data: updatedData, cached_at: Date.now() });
        }
        await db.sync_queue.add({
          action: 'update',
          record_id: id,
          payload: { ...updateData, updated_at: new Date().toISOString() } as Record<string, unknown>,
          queued_at: Date.now(),
        });
        set({ isLoading: false });
        return;
      }

      const { error } = await supabase.from('properties').update(updateData).eq('id', id);
      if (error) throw error;

      // Fetch fresh data to ensure consistency
      await get().fetchProperties();
      if (get().currentProperty?.id === id) await get().fetchProperty(id);
      set({ isLoading: false });
    } catch (error) {
      // Rollback on error
      set({
        properties: previousProperties,
        currentProperty: previousCurrent,
      });

      const message = error instanceof Error ? error.message : 'Fehler beim Aktualisieren';
      console.error('updateProperty error:', error);
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  deleteProperty: async (id) => {
    // Save current state for rollback
    const previousProperties = get().properties;
    const previousCurrent = get().currentProperty;

    set({ isLoading: true, error: null });

    // Optimistic delete - remove from UI immediately
    set((state) => ({
      properties: state.properties.filter((p) => p.id !== id),
      currentProperty: state.currentProperty?.id === id ? null : state.currentProperty,
    }));

    try {
      // Offline: aus IndexedDB löschen + in Sync-Queue einreihen
      if (!navigator.onLine) {
        await db.properties.delete(id);
        await db.sync_queue.add({
          action: 'delete',
          record_id: id,
          payload: {},
          queued_at: Date.now(),
        });
        set({ isLoading: false });
        return;
      }

      const { error } = await supabase.from('properties').delete().eq('id', id);
      if (error) throw error;
      set({ isLoading: false });
    } catch (error) {
      // Rollback on error
      set({
        properties: previousProperties,
        currentProperty: previousCurrent,
      });

      const message = error instanceof Error ? error.message : 'Fehler beim Löschen';
      console.error('deleteProperty error:', error);
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  setFilters: (filters) => {
    set({ filters: { ...get().filters, ...filters } });
    get().fetchProperties();
  },

  clearError: () => set({ error: null }),

  subscribeToChanges: () => {
    // Clean up existing subscription
    const existing = get().subscription;
    if (existing) {
      existing.unsubscribe();
    }

    // Create new subscription for real-time updates
    const channel = supabase
      .channel('properties-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'properties',
        },
        async (payload) => {
          console.log('Real-time change detected:', payload);

          if (payload.eventType === 'INSERT') {
            // Fetch the new property with all relations
            const { data } = await supabase
              .from('properties')
              .select(`*, financings(*), property_images(*)`)
              .eq('id', payload.new.id)
              .single();

            if (data) {
              // Generate signed URLs for images
              if (data.property_images && Array.isArray(data.property_images)) {
                const imagesWithUrls = await Promise.all(
                  data.property_images.map(async (img: PropertyImage) => {
                    const { data: urlData } = await supabase.storage
                      .from('property-images')
                      .createSignedUrl(img.storage_path, 3600);
                    return { ...img, url: urlData?.signedUrl };
                  })
                );
                data.property_images = imagesWithUrls;
                (data as Property).images = imagesWithUrls;
              }

              set((state) => ({
                properties: [data, ...state.properties],
              }));
            }
          } else if (payload.eventType === 'UPDATE') {
            // Refetch to get updated data with relations
            await get().fetchProperties();
          } else if (payload.eventType === 'DELETE') {
            set((state) => ({
              properties: state.properties.filter((p) => p.id !== payload.old.id),
            }));
          }
        }
      )
      .subscribe();

    set({ subscription: channel });
  },

  unsubscribe: () => {
    const existing = get().subscription;
    if (existing) {
      existing.unsubscribe();
      set({ subscription: null });
    }
  },
}));